import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

// Theme: 'men' | 'women' | 'neutral'
export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem('zenvy_theme');
    return stored || 'neutral';
  });

  useEffect(() => {
    localStorage.setItem('zenvy_theme', theme);
    
    // Update body classes
    document.body.classList.remove('theme-men', 'theme-women');
    if (theme === 'men') {
      document.body.classList.add('theme-men');
    } else if (theme === 'women') {
      document.body.classList.add('theme-women');
    }
  }, [theme]);

  const setMenTheme = () => setTheme('men');
  const setWomenTheme = () => setTheme('women');
  const setNeutralTheme = () => setTheme('neutral');

  // Helper to get theme-specific classes
  const getThemeClasses = (type) => {
    const classes = {
      button: {
        men: 'bg-blue-600 hover:bg-blue-700 text-white uppercase tracking-widest font-bold rounded-none',
        women: 'bg-pink-600 hover:bg-pink-700 text-white font-serif italic rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5',
        neutral: 'bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg'
      },
      card: {
        men: 'bg-zinc-900 border border-zinc-800 hover:border-blue-500/50 rounded-none',
        women: 'bg-white border border-pink-100 shadow-sm hover:shadow-md hover:border-pink-200 rounded-2xl',
        neutral: 'bg-white border border-zinc-200 shadow-sm hover:shadow-md rounded-xl'
      },
      heading: {
        men: 'font-heading-men uppercase tracking-wider',
        women: 'font-heading-women italic',
        neutral: ''
      },
      bg: {
        men: 'bg-zinc-950',
        women: 'bg-rose-50',
        neutral: 'bg-white'
      },
      text: {
        men: 'text-zinc-100',
        women: 'text-rose-900',
        neutral: 'text-zinc-900'
      },
      textSecondary: {
        men: 'text-zinc-400',
        women: 'text-rose-600',
        neutral: 'text-zinc-600'
      },
      primary: {
        men: 'text-blue-500',
        women: 'text-pink-600',
        neutral: 'text-zinc-900'
      },
      border: {
        men: 'border-zinc-800',
        women: 'border-pink-200',
        neutral: 'border-zinc-200'
      },
      input: {
        men: 'bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-blue-500',
        women: 'bg-white border-pink-200 text-rose-900 placeholder:text-pink-400 focus:border-pink-500',
        neutral: 'bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-500'
      }
    };
    
    return classes[type]?.[theme] || '';
  };

  return (
    <ThemeContext.Provider value={{
      theme,
      setTheme,
      setMenTheme,
      setWomenTheme,
      setNeutralTheme,
      isMen: theme === 'men',
      isWomen: theme === 'women',
      isNeutral: theme === 'neutral',
      getThemeClasses
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
